import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Shield, Search, UserPlus, Trash2, RefreshCw, Download, Eye, Users,
  ShieldCheck, Headphones, Calculator, Code, Phone, Binoculars, Crown, GraduationCap, Baby,
  CheckCircle2, XCircle, AlertTriangle
} from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { exportToCSV } from "@/lib/export-utils";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// ── Role definitions ──
const allRoles = [
  { value: "super_admin", label: "مشرف عام", color: "bg-amber-100 text-amber-800", icon: Crown, desc: "وصول كامل لجميع بيانات المنصة وإدارة كافة الحضانات", chartColor: "#f59e0b" },
  { value: "support", label: "دعم فني", color: "bg-teal-100 text-teal-800", icon: Headphones, desc: "الرد على التذاكر وحل المشاكل التقنية", chartColor: "#14b8a6" },
  { value: "call_center", label: "كول سنتر", color: "bg-indigo-100 text-indigo-800", icon: Phone, desc: "استقبال المكالمات وتسجيل الشكاوى", chartColor: "#6366f1" },
  { value: "accountant", label: "محاسب", color: "bg-orange-100 text-orange-800", icon: Calculator, desc: "إدارة الاشتراكات والفواتير والتقارير المالية", chartColor: "#f97316" },
  { value: "developer", label: "مطوّر", color: "bg-rose-100 text-rose-800", icon: Code, desc: "تطوير وصيانة المنصة والوصول التقني", chartColor: "#e11d48" },
  { value: "supervisor", label: "مشرف", color: "bg-cyan-100 text-cyan-800", icon: Binoculars, desc: "مراقبة الجودة وعرض بيانات الحضانة بدون تعديل", chartColor: "#06b6d4" },
  { value: "admin", label: "مدير حضانة", color: "bg-blue-100 text-blue-800", icon: ShieldCheck, desc: "إدارة بيانات حضانة واحدة بالكامل", chartColor: "#3b82f6" },
  { value: "teacher", label: "معلم", color: "bg-green-100 text-green-800", icon: GraduationCap, desc: "إدارة الدروس والواجبات والامتحانات", chartColor: "#22c55e" },
  { value: "parent", label: "ولي أمر", color: "bg-purple-100 text-purple-800", icon: Baby, desc: "متابعة الأبناء والتواصل مع الحضانة", chartColor: "#a855f7" },
];

const systemRoles = ["super_admin", "support", "call_center", "accountant", "developer", "supervisor"];
const roleMap = Object.fromEntries(allRoles.map((r) => [r.value, r]));

// ── Permissions matrix ──
const permissionCategories = [
  {
    category: "إدارة الحضانات",
    permissions: [
      { key: "nurseries_view", label: "عرض الحضانات" },
      { key: "nurseries_manage", label: "إنشاء/تعديل/حذف" },
      { key: "nurseries_subscriptions", label: "إدارة الاشتراكات" },
    ],
  },
  {
    category: "المستخدمون والأدوار",
    permissions: [
      { key: "users_view", label: "عرض المستخدمين" },
      { key: "roles_manage", label: "تعيين/حذف الأدوار" },
      { key: "staff_manage", label: "إدارة موظفي النظام" },
    ],
  },
  {
    category: "الدعم والتذاكر",
    permissions: [
      { key: "tickets_view", label: "عرض التذاكر" },
      { key: "tickets_reply", label: "الرد على التذاكر" },
      { key: "tickets_assign", label: "تعيين التذاكر" },
      { key: "calls_manage", label: "سجل المكالمات" },
    ],
  },
  {
    category: "المالية",
    permissions: [
      { key: "invoices_view", label: "عرض الفواتير" },
      { key: "invoices_manage", label: "تعديل الفواتير" },
      { key: "reports_financial", label: "التقارير المالية" },
    ],
  },
  {
    category: "الإعدادات والنظام",
    permissions: [
      { key: "settings_view", label: "عرض الإعدادات" },
      { key: "settings_manage", label: "تعديل الإعدادات" },
      { key: "broadcast", label: "الإشعارات الجماعية" },
      { key: "promo_codes", label: "أكواد الخصم" },
    ],
  },
];

const rolePermissions: Record<string, string[]> = {
  super_admin: permissionCategories.flatMap((c) => c.permissions.map((p) => p.key)),
  support: ["tickets_view", "tickets_reply", "tickets_assign", "calls_manage", "nurseries_view", "users_view"],
  call_center: ["tickets_view", "tickets_reply", "calls_manage"],
  accountant: ["invoices_view", "invoices_manage", "reports_financial", "nurseries_view", "nurseries_subscriptions"],
  developer: ["settings_view", "settings_manage", "nurseries_view", "users_view", "tickets_view"],
  supervisor: ["nurseries_view", "users_view", "tickets_view", "invoices_view", "settings_view"],
};

const AdminPermissions = () => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string; role: string } | null>(null);
  const queryClient = useQueryClient();

  // ── Queries ──
  const { data: roles, isLoading, refetch } = useQuery({
    queryKey: ["admin-all-roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("*, profiles!inner(full_name, phone, user_id, nursery_id)")
        .order("role");
      if (error) throw error;
      return data;
    },
  });

  const { data: profiles } = useQuery({
    queryKey: ["admin-all-profiles-for-roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, phone")
        .order("full_name");
      if (error) throw error;
      return data;
    },
  });

  // ── Mutations ──
  const addRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: role as any });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-all-roles"] });
      toast.success("تم تعيين الدور بنجاح ✅");
      setDialogOpen(false);
      setSelectedUserId("");
      setSelectedRole("");
    },
    onError: (e: any) => {
      toast.error(e.message?.includes("duplicate") || e.code === "23505" ? "هذا المستخدم لديه هذا الدور بالفعل" : "فشل تعيين الدور");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("user_roles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-all-roles"] });
      toast.success("تم حذف الدور بنجاح");
      setConfirmDelete(null);
    },
    onError: () => toast.error("فشل حذف الدور"),
  });

  // ── Computed ──
  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    roles?.forEach((r: any) => { counts[r.role] = (counts[r.role] || 0) + 1; });
    return counts;
  }, [roles]);

  const systemRolesData = useMemo(() => {
    if (!roles) return [];
    return roles.filter((r: any) => systemRoles.includes(r.role));
  }, [roles]);

  const filtered = useMemo(() => {
    if (!systemRolesData) return [];
    return systemRolesData.filter((r: any) => {
      const matchSearch = !search ||
        r.profiles?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.profiles?.phone?.includes(search) ||
        roleMap[r.role]?.label?.includes(search);
      const matchRole = roleFilter === "all" || r.role === roleFilter;
      return matchSearch && matchRole;
    });
  }, [systemRolesData, search, roleFilter]);

  const chartData = useMemo(() =>
    systemRoles.map((r) => ({
      name: roleMap[r]?.label || r,
      value: roleCounts[r] || 0,
      color: roleMap[r]?.chartColor || "#94a3b8",
    })).filter((d) => d.value > 0),
    [roleCounts]
  );

  const handleExport = () => {
    if (!filtered?.length) return;
    exportToCSV(
      filtered.map((r: any) => ({
        المستخدم: r.profiles?.full_name || "",
        الهاتف: r.profiles?.phone || "",
        الدور: roleMap[r.role]?.label || r.role,
      })),
      "system-roles-export.csv"
    );
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            إدارة الصلاحيات والأدوار
          </h1>
          <p className="text-muted-foreground text-sm mt-1">التحكم في أدوار فريق العمل تحت المشرف العام وصلاحيات الوصول</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 ml-1" />
            تحديث
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered?.length}>
            <Download className="w-4 h-4 ml-1" />
            تصدير
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <UserPlus className="w-4 h-4" />
                تعيين دور
              </Button>
            </DialogTrigger>
            <DialogContent dir="rtl" className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>تعيين دور جديد لمستخدم</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label>المستخدم</Label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر مستخدم..." />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles?.map((p) => (
                        <SelectItem key={p.user_id} value={p.user_id}>
                          {p.full_name || "بدون اسم"} {p.phone ? `(${p.phone})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>الدور</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر دور..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allRoles.filter((r) => systemRoles.includes(r.value)).map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          <span className="flex items-center gap-2">
                            <r.icon className="w-4 h-4" />
                            {r.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRole && roleMap[selectedRole] && (
                    <p className="text-xs text-muted-foreground mt-1 p-2 bg-muted rounded">
                      {roleMap[selectedRole].desc}
                    </p>
                  )}
                </div>
                <Button
                  className="w-full"
                  disabled={!selectedUserId || !selectedRole || addRoleMutation.isPending}
                  onClick={() => addRoleMutation.mutate({ userId: selectedUserId, role: selectedRole })}
                >
                  {addRoleMutation.isPending ? "جاري التعيين..." : "تعيين الدور"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {allRoles.filter((r) => systemRoles.includes(r.value)).map((r) => (
          <Card key={r.value} className={`cursor-pointer transition-all hover:shadow-md ${roleFilter === r.value ? "ring-2 ring-primary" : ""}`}
            onClick={() => setRoleFilter(roleFilter === r.value ? "all" : r.value)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${r.color}`}>
                <r.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{roleCounts[r.value] || 0}</p>
                <p className="text-xs text-muted-foreground">{r.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="members" className="space-y-4">
        <TabsList>
          <TabsTrigger value="members">أعضاء الفريق</TabsTrigger>
          <TabsTrigger value="matrix">مصفوفة الصلاحيات</TabsTrigger>
          <TabsTrigger value="charts">الإحصائيات</TabsTrigger>
        </TabsList>

        {/* Tab: Members Table */}
        <TabsContent value="members">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <CardTitle className="text-base">فريق العمل ({filtered?.length || 0})</CardTitle>
                </div>
                <div className="flex-1" />
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                  <div className="relative flex-1 md:flex-none md:w-56">
                    <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="بحث بالاسم أو الهاتف..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="الدور" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الأدوار</SelectItem>
                      {allRoles.filter((r) => systemRoles.includes(r.value)).map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="mr-2 text-muted-foreground">جاري التحميل...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>المستخدم</TableHead>
                      <TableHead>الهاتف</TableHead>
                      <TableHead>الدور</TableHead>
                      <TableHead>الوصف</TableHead>
                      <TableHead className="w-16">إجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered?.map((role: any, idx: number) => {
                      const rd = roleMap[role.role];
                      const Icon = rd?.icon || Shield;
                      return (
                        <TableRow key={role.id}>
                          <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                                {role.profiles?.full_name?.charAt(0) || "؟"}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{role.profiles?.full_name || "بدون اسم"}</p>
                                <p className="text-[10px] text-muted-foreground">{role.user_id?.slice(0, 8)}...</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell dir="ltr" className="text-sm">{role.profiles?.phone || "—"}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${rd?.color || "bg-muted"}`}>
                              <Icon className="w-3 h-3" />
                              {rd?.label || role.role}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{rd?.desc || "—"}</TableCell>
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setConfirmDelete({ id: role.id, name: role.profiles?.full_name || "—", role: rd?.label || role.role })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filtered?.length && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                          لا توجد أدوار مطابقة للبحث
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Permissions Matrix */}
        <TabsContent value="matrix">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-5 h-5" />
                مصفوفة الصلاحيات حسب الدور
              </CardTitle>
              <p className="text-xs text-muted-foreground">توضيح تفصيلي لصلاحيات كل دور في النظام</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px] sticky right-0 bg-background z-10">الصلاحية</TableHead>
                      {allRoles.filter((r) => systemRoles.includes(r.value)).map((r) => (
                        <TableHead key={r.value} className="text-center min-w-[90px]">
                          <div className="flex flex-col items-center gap-1">
                            <r.icon className="w-4 h-4" />
                            <span className="text-[10px]">{r.label}</span>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {permissionCategories.map((cat) => (
                      <>
                        <TableRow key={cat.category}>
                          <TableCell colSpan={systemRoles.length + 1} className="bg-muted/50 font-semibold text-xs py-2">
                            {cat.category}
                          </TableCell>
                        </TableRow>
                        {cat.permissions.map((perm) => (
                          <TableRow key={perm.key}>
                            <TableCell className="text-sm sticky right-0 bg-background">{perm.label}</TableCell>
                            {systemRoles.map((role) => (
                              <TableCell key={role} className="text-center">
                                {rolePermissions[role]?.includes(perm.key) ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-muted-foreground/30 mx-auto" />
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Charts */}
        <TabsContent value="charts">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">توزيع الأدوار</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                      {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">عدد الأعضاء لكل دور</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={systemRoles.map((r) => ({
                    name: roleMap[r]?.label || r,
                    count: roleCounts[r] || 0,
                    fill: roleMap[r]?.chartColor || "#94a3b8",
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="أعضاء" radius={[6, 6, 0, 0]}>
                      {systemRoles.map((r, i) => <Cell key={i} fill={roleMap[r]?.chartColor || "#94a3b8"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Role descriptions */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">وصف الأدوار ونطاق العمل</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allRoles.filter((r) => systemRoles.includes(r.value)).map((r) => (
                    <div key={r.value} className={`p-4 rounded-xl border-2 ${r.color} border-transparent`}>
                      <div className="flex items-center gap-2 mb-2">
                        <r.icon className="w-5 h-5" />
                        <span className="font-semibold">{r.label}</span>
                        <span className="text-xs font-mono opacity-60">({r.value})</span>
                      </div>
                      <p className="text-sm opacity-80">{r.desc}</p>
                      <p className="text-xs mt-2 font-medium">
                        {rolePermissions[r.value]?.length || 0} صلاحية
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent dir="rtl" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              تأكيد حذف الدور
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              هل أنت متأكد من حذف دور <span className="font-bold">{confirmDelete?.role}</span> من المستخدم <span className="font-bold">{confirmDelete?.name}</span>؟
            </p>
            <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
              سيفقد المستخدم جميع الصلاحيات المرتبطة بهذا الدور فوراً.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDelete(null)}>إلغاء</Button>
              <Button variant="destructive" className="flex-1" disabled={deleteMutation.isPending}
                onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}>
                {deleteMutation.isPending ? "جاري الحذف..." : "حذف الدور"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPermissions;
